import PeriodFilter from 'components/Billing/PeriodFilter';
import SummaryCards from 'components/Billing/SummaryCards';
import { RenegotiationTag } from 'components/Billing/renegotiationTag';
import BasicTable from 'components/Form/Table';
import TableWrapper from 'components/TableWrapper';
import ContainerAuthenticated from 'containers/Authenticated';
import useSettlementReport from 'hooks/useSettlementReport';
import { useMemo } from 'react';
import {
  SETTLEMENT_EXPORT_COLUMNS,
  SETTLEMENT_TYPE_OPTIONS,
  formatIsoDate,
  money,
} from 'utils/billingReports';
import { downloadSpreadsheetCSV } from 'utils/downloads';

// Etiqueta do tipo de baixa: pagamento sai em texto, as baixas sem pagamento
// usam as mesmas etiquetas da coluna Parcela das telas do menu.
const TYPE_TAGS = {
  renegociado: { variant: 'renegotiated', icon: 'call_split' },
  cancelado: { variant: 'cancelled', icon: 'block' },
};

const TypeCell = ({ row }) => {
  const tag = TYPE_TAGS[row?.tipo_baixa];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
      {!tag ? (
        row?.tipo_baixa_label
      ) : (
        <RenegotiationTag {...tag} label={row?.tipo_baixa_label} />
      )}
      {!row?.mora_dispensada ? null : (
        <RenegotiationTag
          variant="renegotiated"
          icon="money_off"
          label="Mora dispensada"
        />
      )}
    </span>
  );
};

// Dias entre o vencimento e a baixa: positivo e atraso, negativo e antecipado.
const DaysCell = ({ row }) => {
  const days = row?.dias_pagamento;
  if (!Number.isFinite(days)) {
    return '';
  }
  if (days === 0) {
    return 'No vencimento';
  }
  return days > 0 ? (
    <span style={{ color: '#FF9500', fontWeight: 600 }}>
      {days} dia{days === 1 ? '' : 's'} de atraso
    </span>
  ) : (
    `${-days} dia${days === -1 ? '' : 's'} antes`
  );
};

const COLUMNS = [
  {
    title: 'Data da baixa',
    ref: 'data_baixa',
    sortable: true,
    renderCell: ({ row }) => formatIsoDate(row?.data_baixa),
  },
  { title: 'Contrato', ref: 'contrato', sortable: true },
  { title: 'Cliente', ref: 'customer', sortable: true },
  { title: 'Distribuidora', ref: 'distributor', sortable: true },
  { title: 'Banco', ref: 'banco', sortable: true },
  { title: 'Parcela', ref: 'parcela' },
  {
    title: 'Vencimento',
    ref: 'data_vencimento',
    sortable: true,
    renderCell: ({ row }) => formatIsoDate(row?.data_vencimento),
  },
  {
    title: 'Pagamento',
    ref: 'dias_pagamento',
    sortable: true,
    renderCell: DaysCell,
  },
  {
    title: 'PMT',
    ref: 'valor',
    sortable: true,
    renderCell: ({ row }) => row?.valor_fmt,
  },
  {
    title: 'Encargos',
    ref: 'encargos',
    sortable: true,
    renderCell: ({ row }) =>
      !row?.encargos ? (
        ''
      ) : (
        <span
          title={`Juros ${money(row.juros)} · Multa ${money(
            row.multa
          )} · IOF ${money(row.iof)}`}
        >
          {row.encargos_fmt}
        </span>
      ),
  },
  {
    title: 'Desconto',
    ref: 'desconto',
    sortable: true,
    renderCell: ({ row }) =>
      !row?.desconto
        ? ''
        : `${row.desconto_fmt}${
            Number.isFinite(row?.perc_desconto)
              ? ` (${`${row.perc_desconto}`.replace('.', ',')}%)`
              : ''
          }`,
  },
  {
    title: 'Recebido',
    ref: 'valor_recebido',
    sortable: true,
    renderCell: ({ row }) => <b>{row?.valor_recebido_fmt}</b>,
  },
  { title: 'Tipo', ref: 'tipo_baixa_label', renderCell: TypeCell },
  { title: 'Operador', ref: 'operador', sortable: true },
  { title: 'Origem', ref: 'origem', sortable: true },
];

// Relatorio de baixas do menu Cobranca: os titulos baixados num periodo, pela
// `data_baixa` (a data do pagamento, e nao a do registro). Le `GET /titulos`,
// ver o hook. A exportacao leva todas as colunas da linha, inclusive as que
// nao cabem na tela.
export default function BillingRelatorioBaixas() {
  const {
    loading,
    period,
    setPeriod,
    validRange,
    type,
    setType,
    visibleRows,
    cards,
  } = useSettlementReport();

  const buttons = useMemo(
    () => [
      {
        label: 'Exportar',
        icon: 'download',
        color: 'blue',
        border: true,
        action: () =>
          downloadSpreadsheetCSV(
            SETTLEMENT_EXPORT_COLUMNS,
            visibleRows,
            `baixas_${period.from}_a_${period.to}.csv`
          ),
      },
    ],
    [visibleRows, period]
  );

  return (
    <ContainerAuthenticated>
      <TableWrapper
        title="Relatório de Baixas"
        subTitle="Títulos baixados no período, pela data do pagamento"
        search
        buttons={buttons}
        filters={
          <PeriodFilter
            label="Data da baixa"
            period={period}
            onChangePeriod={setPeriod}
            invalid={!validRange}
            options={SETTLEMENT_TYPE_OPTIONS}
            value={type}
            onChange={setType}
          />
        }
      >
        <SummaryCards cards={cards} />
        <BasicTable
          loading={loading}
          columns={COLUMNS}
          rows={visibleRows}
          pagination
        />
      </TableWrapper>
    </ContainerAuthenticated>
  );
}
